import { useEffect, useRef } from "react";
import { useStore, useSyncStatus } from "../../data/StoreContext";

/**
 * Enters every scheduled occurrence due by today, once per app open (SPEC §6: the daily job
 * "also runs when a client opens"), but only once this device has synced in this session: a
 * copy that has been offline since before another device (or the server job) entered them
 * would otherwise enter them again. IDs are derived, so a race still merges; this keeps the
 * stale copy from re-entering occurrences that were since skipped or edited elsewhere.
 * `everSynced` is remembered across opens, so it only counts when it turns true now.
 */
export function useEnterDueScheduledOnOpen(): void {
  const store = useStore();
  const { loaded, connection, unsynced, everSynced } = useSyncStatus();
  const everSyncedAtOpen = useRef(everSynced);
  const synced = useRef(false);
  const ran = useRef(false);
  useEffect(() => {
    if ((connection === "online" && unsynced === 0) || (everSynced && !everSyncedAtOpen.current)) {
      synced.current = true;
    }
    if (!loaded || !synced.current || ran.current) {
      return;
    }
    ran.current = true;
    void import("./enterDue").then(({ enterDueOnOpen }) => enterDueOnOpen(store));
  }, [loaded, connection, unsynced, everSynced, store]);
}
